import autobind from 'autobind-decorator';
import * as React from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';
import {Button, Icon, notification, Select, Spin} from 'antd';
import {connect} from 'redux-scaffolding-ts';
import {Link, RouteComponentProps, withRouter} from 'react-router-dom';
import {ProjectMembersStore, ProjectMemberSummary} from 'src/stores/projectmembers-store';
import {ItemState, Query} from 'src/core/stores/dataStore';
import {TableModel, TableView} from 'src/core/ui/collections/table';
import {formatMessage} from 'src/core/services/http.service';
import {CommandResult} from 'src/core/stores/types';
import NewProjectMemberView from './projectmember-new';
import CreateAllMemberView from './projectmember-create-all';
import EditableTooltip from 'src/components/editable-tooltip/tooltip';
import {CacheProps, withCache} from 'src/core/services/cache.service';
import {withIdentity} from '../../../../core/services/authentication';
import {UserProfileProps, withUserProfile} from "../../../../components/user-profile";

const { Option } = Select;

// tslint:disable-next-line:no-empty-interface
interface ProjectMembersViewProps extends RouteComponentProps<{ companyId: string, projectId: string }>, WithTranslation, CacheProps, UserProfileProps {
    projectName: string | undefined;
    onRefreshRequired?: () => void;
}

interface ProjectMembersViewState {
    query: Query;
    newProjectMemberShown: boolean;
    showCreateAllModal: boolean;
}

@connect(['projectMembers', ProjectMembersStore])
class ProjectMembersDashboard extends React.Component<ProjectMembersViewProps, ProjectMembersViewState> {
    private get projectMembersStore() {
        return (this.props as any).projectMembers as ProjectMembersStore;
    }

    constructor(props: ProjectMembersViewProps) {
        super(props);

        var queryStored = props.cache.getWithCustomKey("Project-Members")

        var query = { searchQuery: '', orderBy: [{ field: 'title', direction: 'Ascending', useProfile: true }], skip: 0, take: 10 }

        if (queryStored)
            query = queryStored
        this.state = {
            query: query as any ,
            newProjectMemberShown: false,
            showCreateAllModal: false
        };
    }

    componentWillMount() {
        this.projectMembersStore.changeUrlByProject(this.props.match.params.companyId, this.props.match.params.projectId);
        this.load(this.state.query);
    }

    @autobind
    private load(query: Query) {
        this.props.cache.saveWithCustomKey("Project-Members", query);
        this.projectMembersStore.getAllAsync(query);
    }

    @autobind
    private isReadOnly() {
        return (this.props.userProfile && this.props.userProfile.roles &&
            (this.props.userProfile.roles.includes("CompanyDataViewer")
                && !(this.props.userProfile.roles.includes("Manager")
                    || this.props.userProfile.roles.includes("Administrator")
                    || this.props.userProfile.roles.includes("ClientAdmin")
                    || this.props.userProfile.roles.includes("FIConsultant"))));
    }

    @autobind
    private onQueryChanged(query: Query) {
        this.setState({ query }, () => this.load(query));
    }

    @autobind
    private countDownNotification(_notification: any, message: string, description: string, key: string) {
        let secondsToGo = 4;
        _notification({
            key: key,
            message: message + `(${secondsToGo}).`,
            description: description
        });
        const timer = setInterval(() => {
            secondsToGo -= 1;
            _notification({
                key: key,
                message: message + `(${secondsToGo}).`,
                description: description,
                onClose: () => clearInterval(timer),
            });
        }, 1000);
        setTimeout(() => {
            clearInterval(timer);
            notification.close(key);
        }, secondsToGo * 1000);
    }

    @autobind
    private async onSaveItem(item: ProjectMemberSummary, state: ItemState) {
        const { t } = this.props as any;
        return await this.projectMembersStore.saveAsync(`${item.id}`, item, state)
            .then((result) => {
                this.countDownNotification(notification.success, t("Notification"), t("All changes were successfully saved"), "succes");
                return result;
            })
            .catch(error => {
                this.countDownNotification(notification.error, t("Error"), t("There was an error saving changes"), "error");
                return error;
            });
    }

    @autobind
    private async onDeleteRow(item: ProjectMemberSummary, state: ItemState): Promise<CommandResult<any>> {
        var del = await this.projectMembersStore.deleteAsync(`${item.id}`)
        if (this.props.onRefreshRequired) {
            this.props.onRefreshRequired()
        }
        return del;
    }

    @autobind
    private onNewItem() {
        this.setState({ newProjectMemberShown: true });
    }

    @autobind
    private onNewItemClosed(id: string | undefined) {
        this.setState({ newProjectMemberShown: false, showCreateAllModal: false });

        if (id !== undefined)
            this.load(this.state.query);
    }

    @autobind
    private addAllEmployees() {
        this.setState({ showCreateAllModal: true })
    }

    public render() {
        const { t } = this.props as any;
        let isReadOnly = this.isReadOnly()
        const tableModel = {
            query: this.state.query,
            columns: [
                {
                    sortable: true,
                    field: 'title',
                    title: t('Employees'),
                    renderer: (data) =>
                        <span>
                            <Link
                                to={{
                                pathname: `/time-tracking/admin/companies/${this.props.match.params.companyId}/projects/${this.props.match.params.projectId}/${data.employeeId}`,
                                state: {
                                    titles:
                                        [...(this.props.location.state ? (this.props.location.state as any).titles || [] : []), {
                                            url: `/time-tracking/admin/companies/${this.props.match.params.companyId}/projects/${this.props.match.params.projectId}`, title: this.props.projectName
                                        }]
                                }
                            }}
                            >
                                <Icon type="user" />&nbsp;
                                <span>{data.employeeTitle.split('(')[0]}</span>
                                <span style={{ fontSize: 12 }}>({data.employeeTitle.split('(')[1]}</span>
                            </Link>
                        </span>,
                },
                {
                    sortable: true,
                    field: 'permission',
                    title: t('Permissions'),
                    renderer: (data) => <span>
                        {data.permission == 0 && <span>{t("None")}</span>}
                        {data.permission == 1 && <span>{t("Reader")}</span>}
                        {data.permission == 2 && <span>{t("Writer")}</span>}
                        {data.permission == 4 && <span>{t("Owner")}</span>}
                        {data.permission == 7 && <span>{t("Project Manager")}</span>}
                    </span>,
                    editor: (data) => (
                        <Select style={{ width: 150 }}>
                            <Option key={1} value={1}> {t("Reader")} </Option>
                            {data.permission == 4 && <Option key={4} value={4}> {t("Owner")} </Option>}
                            <Option key={7} value={7}> {t("Project Manager")} </Option>
                        </Select>
                    )
                }
            ],
            data: this.projectMembersStore.state,
            sortFields: [{
                field: 'title',
                text: t('Employee'),
                useProfile: true
            },
                {
                    field: 'permission',
                    text: <EditableTooltip name='member-permission'> {t('Permissions')} </EditableTooltip>,
                    useProfile: true
                }],
        } as TableModel<ProjectMemberSummary>;
        return <Spin size='large' spinning={this.projectMembersStore.state.isBusy}>
            <TableView
                showDeleteStatusWindow
                rowKey='id'
                model={tableModel}
                error={this.projectMembersStore.state.result && !this.projectMembersStore.state.result.isSuccess && formatMessage(this.projectMembersStore.state.result)}
                embedded={false}
                onQueryChanged={(q: Query) => this.onQueryChanged(q)}
                onRefresh={() => this.load(this.state.query)}
                canEdit={!isReadOnly}
                onSaveRow={this.onSaveItem}
                canDelete={!isReadOnly}
                onDeleteRow={this.onDeleteRow}
                canCreateNew={!isReadOnly}
                onNewItem={this.onNewItem}
                leftToolbar={(
                    <ul>
                        <li>
                            <EditableTooltip name='project-members-add-all-employees' noIcon>
                                <Button style={{ marginLeft: -40 }} disabled={isReadOnly} onClick={() => this.setState({ showCreateAllModal:true })} icon={'usergroup-add'}>
                                    <Icon type="info-circle" style={{ fontSize: '12px', color: 'grey', marginRight: 3, marginLeft: 3 }} />
                                </Button>
                            </EditableTooltip>
                        </li>
                    </ul>
                )} />
                <CreateAllMemberView
                    visible={this.state.showCreateAllModal}
                    companyId={this.props.match.params.companyId}
                    projectId={this.props.match.params.projectId}
                    onRefreshRequired={this.props.onRefreshRequired}
                    onClose={this.onNewItemClosed}
            />
            {this.state.newProjectMemberShown && <NewProjectMemberView companyId={this.props.match.params.companyId} projectId={this.props.match.params.projectId} onRefreshRequired={this.props.onRefreshRequired} onClose={this.onNewItemClosed} onAddAllEmployees={() => this.setState({ showCreateAllModal: true, newProjectMemberShown: false })} />}
        </Spin>
    }
}

// Wire up the React component to the Redux store
export default withUserProfile(withCache(withTranslation()(withIdentity(withRouter(ProjectMembersDashboard)))));
